import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController, IonInput } from '@ionic/angular/lazy';
import { QuoteService } from 'src/app/services/quote.service';
import { ObserverService } from 'src/app/services/observer.service';

@Component({
    selector: 'app-quote-email',
    templateUrl: './quote-email.page.html',
    styleUrls: ['./quote-email.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class QuoteEmailPage implements OnInit {

    @ViewChild('to_email_address_input', { static: false }) to_email_address_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    @Input() quote_id = null;
    quote = null;
    params = {};

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private quoteService: QuoteService,
        private observerService: ObserverService
    ) { }

    ngOnInit() {

        this.loadData().then(() => {

            if(this.quote.organisation && this.quote.organisation.billing_person && this.quote.organisation.billing_person.email_address) {
                this.params["to_email_address"] = this.quote.organisation.billing_person.email_address;
            }
            else if(this.quote.person && this.quote.person.email_address) {
                this.params["to_email_address"] = this.quote.person.email_address;
            }

            this.params["subject"] = "Quote #" + this.quote.number;

        })
        .catch(() => { });

        this.observerService.subscribe('network_status_changed|quote_email_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to email a quote please check your network connection and try again.");
                }
            }
        });

    }

    ionViewDidEnter() {
        // NOTE: we use a timeout here so the DOM gets a chance to load
        if(!this.params["to_email_address"]) {
            setTimeout(() => {
                if(this.to_email_address_input) {
                    this.to_email_address_input.setFocus();
                }
            }, 200);
        }
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading quote").then(() => {
                this.loadQuote().then(() => {
                    this.loading_spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|quote_email_modal");
    }

    loadQuote() {
        return new Promise<void>((resolve, reject) => {
            this.quote = null;
            this.quoteService.getQuote(this.quote_id).then((quote) => {
                if(quote) {
                    this.quote = quote;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    emailQuote() {

        let validated = true;
        let email_address_regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if(this.params['to_email_address'] == null || this.params['to_email_address'].trim() === "") {
            this.presentAlert("Missing email address", "Please enter an email address.");
            validated = false;
        }
        else if(this.params["to_email_address"].length && !email_address_regex.test(String(this.params["to_email_address"]).toLowerCase())) {
            this.presentAlert("Invalid email address", "The email address you've entered appears to be invalid.");
            validated = false;
        }

        if(this.params['subject'] == null || this.params['subject'].trim() === "") {
            this.presentAlert("Missing subject", "Please enter a subject.");
            validated = false;
        }

        if(validated) {
            this.sendQuoteEmail().then(() => {
                this.modalController.dismiss();
            });
        }

    }

    sendQuoteEmail () {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Sending quote email").then(() => {
                this.quoteService.sendQuoteEmail(this.quote_id, this.params).then(() =>{
                    this.spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.spinner.dismiss();
                    reject();
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
